import {
  useFetch,
  useNotificationContext,
  UsePrivileges,
} from "@4uhub/lib4uhub";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import SendTimeExtensionRoundedIcon from "@mui/icons-material/SendTimeExtensionRounded";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { IExtensionInstall } from "../../models/extensions-store";
import MainApiService from "../../services/mainApi.service";
import ExtensionInstallPopper from "./ExtensionInstallPopper";

const translationPath = "components.extensionsStoreDetail.";

const EXTENSION_INSTALL_ROUTE =
  process.env.REACT_APP_MAIN + "/api/v1/SiteAppExtension/InstallExtension";
const extensionInstallService = new MainApiService<IExtensionInstall>(
  EXTENSION_INSTALL_ROUTE
);

interface IExtensionDetailInstall {
  siteAppId: string;
  extensionId: string;
  isDrawerOpen: boolean;
  isRequireSetup: boolean;
  onRealoadExtensionsList: () => void;
  toggleDetail: (open: boolean) => void;
  onToggleExtensionsOption: () => void;
}

const ExtensionDetailInstall = ({
  siteAppId,
  extensionId,
  isDrawerOpen,
  isRequireSetup,
  onRealoadExtensionsList,
  onToggleExtensionsOption,
  toggleDetail,
}: IExtensionDetailInstall) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { sendRequest, loading } = useFetch(extensionInstallService.create);

  const { setMessage } = useNotificationContext();

  const { privileges } = UsePrivileges();

  const isMaster = privileges.find(({ name }) => name === "platform-all");

  const onInstallExtension = useCallback(
    async (values: IExtensionInstall) => {
      const { data, success } = await sendRequest({
        siteAppIds: values.siteAppIds,
        extensionId: values.extensionId,
      });
      if (data && success) {
        onRealoadExtensionsList();
        toggleDetail(false);
        onToggleExtensionsOption();
        setMessage({
          notificationKey: new Date().getMilliseconds(),
          message:
            isRequireSetup && !isMaster
              ? data.message
              : `${t("page.extensionsStore.installed_successfully")}`,
          type: "success",
        });
      }
    },
    [
      sendRequest,
      t,
      onRealoadExtensionsList,
      toggleDetail,
      onToggleExtensionsOption,
      setMessage,
      isRequireSetup,
      isMaster,
    ]
  );

  useEffect(() => {
    if (!isDrawerOpen) {
      setOpen(false);
    }
  }, [isDrawerOpen]);

  const onClose = () => setOpen(false);

  return (
    <>
      <ExtensionInstallPopper
        open={open}
        isRequireSetup={isRequireSetup}
        anchorEl={anchorEl}
        onClose={onClose}
        onConfirm={() => {
          onClose();
          onInstallExtension({
            siteAppIds: [siteAppId],
            extensionId: extensionId,
          });
        }}
      />
      <Tooltip
        title={t(
          translationPath +
            `${
              isRequireSetup && !isMaster
                ? "request_install"
                : "tooltip_install"
            }`
        )}
      >
        <IconButton
          size="small"
          color="primary"
          disabled={loading}
          onClick={(event) => {
            setAnchorEl(event.currentTarget);
            setOpen(true);
          }}
        >
          {loading && <CircularProgress size={18} />}
          {!loading && isMaster && <DownloadRoundedIcon />}
          {!loading && isRequireSetup && !isMaster && (
            <SendTimeExtensionRoundedIcon />
          )}
        </IconButton>
      </Tooltip>
    </>
  );
};

export default ExtensionDetailInstall;
